import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import { useAuth } from '@/contexts/AuthContext';
import mmrService, { MMRLeaderboardResponseData } from '@/services/mmrService';

const POSITION_FILTERS = [
  { id: '', label: 'Semua', icon: 'ph:users-bold' },
  { id: 'Defender', label: 'Defender', icon: 'ph:shield-bold' },
  { id: 'Midfielder', label: 'Midfielder', icon: 'ph:arrows-left-right-bold' },
  { id: 'Forward', label: 'Forward', icon: 'ph:soccer-ball-bold' },
  { id: 'Goalkeeper', label: 'Goalkeeper', icon: 'ph:hand-palm-bold' },
];

const getInitials = (name: string): string => {
  if (!name) return 'P';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const LeaderboardSkeleton: React.FC = () => (
  <div className="space-y-5 animate-pulse">
    {/* Top 3 Podium Skeleton */}
    <div className="bg-gradient-to-b from-[#1E293B] to-[#0F172A] rounded-3xl p-5 md:p-6 text-white shadow-xl space-y-4 border border-slate-800">
      <div className="flex items-center justify-between">
        <div className="h-3.5 w-16 bg-slate-700/80 rounded" />
        <div className="h-3.5 w-16 bg-slate-700/80 rounded" />
      </div>
      <div className="grid grid-cols-3 items-end gap-2 pt-2 pb-1 text-center">
        {/* Left #2 */}
        <div className="flex flex-col items-center space-y-2">
          <div className="w-14 h-14 aspect-square rounded-full bg-slate-700/80 shrink-0" />
          <div className="h-3 w-16 bg-slate-700/80 rounded" />
          <div className="h-4 w-12 bg-slate-700/80 rounded" />
        </div>
        {/* Center #1 */}
        <div className="flex flex-col items-center space-y-2 -translate-y-2">
          <div className="w-16 h-16 aspect-square rounded-full bg-slate-700/80 shrink-0" />
          <div className="h-3 w-20 bg-slate-700/80 rounded" />
          <div className="h-4 w-14 bg-slate-700/80 rounded" />
        </div>
        {/* Right #3 */}
        <div className="flex flex-col items-center space-y-2">
          <div className="w-14 h-14 aspect-square rounded-full bg-slate-700/80 shrink-0" />
          <div className="h-3 w-16 bg-slate-700/80 rounded" />
          <div className="h-4 w-12 bg-slate-700/80 rounded" />
        </div>
      </div>
    </div>

    {/* Posisi Filter Chips Skeleton */}
    <div className="space-y-2">
      <div className="h-3 w-12 bg-gray-200 rounded" />
      <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
        {[80, 96, 104, 88, 100].map((w, idx) => (
          <div key={idx} className="h-8 rounded-full bg-gray-200 shrink-0" style={{ width: `${w}px` }} />
        ))}
      </div>
    </div>

    {/* Table List Skeleton */}
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="h-5 w-36 bg-gray-200 rounded" />
        <div className="h-4 w-24 bg-gray-200 rounded" />
      </div>

      <div className="bg-white rounded-3xl border border-gray-100 shadow-2xs overflow-hidden divide-y divide-gray-100">
        <div className="flex items-center px-4 py-2.5 bg-gray-50/80 text-[10px] font-bold text-gray-300 uppercase tracking-wider">
          <span className="w-8 text-center">#</span>
          <span className="flex-1 pl-2">PEMAIN</span>
          <span className="w-14 text-center">MATCH</span>
          <span className="w-16 text-right">MMR</span>
        </div>

        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="flex items-center px-4 py-3.5 space-x-3">
            <div className="w-6 h-4 bg-gray-200 rounded" />
            <div className="w-10 h-10 aspect-square rounded-full bg-gray-200 shrink-0" />
            <div className="flex-1 space-y-1.5 min-w-0">
              <div className="h-4 w-32 bg-gray-200 rounded" />
              <div className="h-3 w-20 bg-gray-100 rounded" />
            </div>
            <div className="w-10 h-4 bg-gray-200 rounded" />
            <div className="w-14 h-4 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    </div>

    {/* User Card Skeleton */}
    <div className="bg-[#0F172A] rounded-2xl p-4 shadow-lg flex items-center justify-between border border-slate-800">
      <div className="flex items-center gap-3">
        <div className="w-7 h-5 bg-slate-700/80 rounded" />
        <div className="w-10 h-10 aspect-square rounded-full bg-slate-700/80 shrink-0" />
        <div className="space-y-1.5">
          <div className="h-3.5 w-28 bg-slate-700/80 rounded" />
          <div className="h-3 w-36 bg-slate-700/80 rounded" />
        </div>
      </div>
      <div className="w-12 h-5 bg-slate-700/80 rounded" />
    </div>
  </div>
);

const ClientMMRLeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [selectedPosition, setSelectedPosition] = useState<string>('');
  const [leaderboardData, setLeaderboardData] = useState<MMRLeaderboardResponseData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchLeaderboard = async (pos: string) => {
    try {
      setIsLoading(true);
      const res = await mmrService.getLeaderboard(pos, 'invasion', { public: !isAuthenticated });
      if (res.success && res.data) {
        setLeaderboardData(res.data);
      }
    } catch (err: any) {
      showToast.error(err?.response?.data?.message || 'Gagal memuat papan peringkat');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboard(selectedPosition);
  }, [selectedPosition]);

  const top3 = leaderboardData?.top3 || [];
  const rankings = leaderboardData?.rankings || [];
  const currentUser = leaderboardData?.currentUser;
  const diffNextText = leaderboardData?.diffNextText || '160 MMR dari #5';

  const rank1 = top3.find((p) => p.rank === 1) || top3[0];
  const rank2 = top3.find((p) => p.rank === 2) || top3[1];
  const rank3 = top3.find((p) => p.rank === 3) || top3[2];

  return (
    <div className="bg-sporto-bg min-h-screen text-sporto-dark py-4 md:py-5 pb-12">
      {/* Desktop Breadcrumb Header (Hidden on Mobile as MobileTopbar handles topbar) */}
      <div className="hidden md:block bg-white border-b border-[#E0E6ED] px-6 py-4 mb-6 shadow-2xs">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="w-9 h-9 rounded-full bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-700 hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <Icon icon="ph:arrow-left-bold" className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-800">Papan Peringkat</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 space-y-5">
        {isLoading ? (
          <LeaderboardSkeleton />
        ) : (
          <>
            {/* Top 3 Podium Card */}
          <div className="bg-gradient-to-b from-[#1E293B] to-[#0F172A] rounded-3xl p-5 md:p-6 text-white shadow-xl space-y-4 border border-slate-800">
            <div className="flex items-center justify-between text-xs font-semibold">
              <span className="uppercase tracking-wider text-slate-400">TOP 3</span>
              <span className="text-slate-400">Musim ini</span>
            </div>

            <div className="grid grid-cols-3 items-end gap-2 pt-2 pb-1 text-center">
              {/* Rank 2 (Left) */}
              <div
                onClick={() => rank2?.username && navigate(`/profile/${rank2.username}`)}
                className={`flex flex-col items-center space-y-1.5 ${rank2?.username ? 'cursor-pointer hover:opacity-85 transition-opacity' : ''}`}
              >
                <div className="relative">
                  {rank2?.avatar ? (
                    <img
                      src={rank2.avatar}
                      alt={rank2.name}
                      className="w-14 h-14 aspect-square rounded-full object-cover shrink-0 border-2 border-blue-400 shadow-md"
                    />
                  ) : (
                    <div className="w-14 h-14 aspect-square rounded-full shrink-0 bg-blue-500/80 text-white font-bold flex items-center justify-center text-sm border-2 border-blue-400 shadow-md">
                      {getInitials(rank2?.name || 'Player 2')}
                    </div>
                  )}
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center border border-slate-900 shadow">
                    2
                  </span>
                </div>
                <div className="pt-1">
                  <p className="text-xs font-bold text-white truncate max-w-[90px]">
                    {rank2?.name || 'Player 2'}
                  </p>
                  <p className="text-sm font-extrabold text-white mt-0.5">
                    {Math.round(rank2?.mmr || 0).toLocaleString('id-ID')}
                  </p>
                  <span className="inline-block text-[10px] text-slate-400 font-medium">
                    {rank2?.tier || 'Silver'}
                  </span>
                </div>
              </div>

              {/* Rank 1 (Center - Elevated) */}
              <div
                onClick={() => rank1?.username && navigate(`/profile/${rank1.username}`)}
                className={`flex flex-col items-center space-y-1.5 -translate-y-2 ${rank1?.username ? 'cursor-pointer hover:opacity-85 transition-opacity' : ''}`}
              >
                <div className="relative">
                  {rank1?.avatar ? (
                    <img
                      src={rank1.avatar}
                      alt={rank1.name}
                      className="w-16 h-16 aspect-square rounded-full object-cover shrink-0 border-3 border-amber-400 shadow-lg shadow-amber-500/20"
                    />
                  ) : (
                    <div className="w-16 h-16 aspect-square rounded-full shrink-0 bg-amber-500 text-white font-extrabold flex items-center justify-center text-base border-3 border-amber-400 shadow-lg shadow-amber-500/20">
                      {getInitials(rank1?.name || 'Player 1')}
                    </div>
                  )}
                  <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-amber-400 text-slate-950 text-xs font-black w-6 h-6 rounded-full flex items-center justify-center border-2 border-slate-900 shadow">
                    1
                  </span>
                </div>
                <div className="pt-1">
                  <p className="text-xs font-bold text-white truncate max-w-[100px]">
                    {rank1?.name || 'Player 1'}
                  </p>
                  <p className="text-base font-extrabold text-amber-300 mt-0.5">
                    {Math.round(rank1?.mmr || 0).toLocaleString('id-ID')}
                  </p>
                  <span className="inline-block text-[10px] text-slate-400 font-medium">
                    {rank1?.tier || 'Silver'}
                  </span>
                </div>
              </div>

              {/* Rank 3 (Right) */}
              <div
                onClick={() => rank3?.username && navigate(`/profile/${rank3.username}`)}
                className={`flex flex-col items-center space-y-1.5 ${rank3?.username ? 'cursor-pointer hover:opacity-85 transition-opacity' : ''}`}
              >
                <div className="relative">
                  {rank3?.avatar ? (
                    <img
                      src={rank3.avatar}
                      alt={rank3.name}
                      className="w-14 h-14 aspect-square rounded-full object-cover shrink-0 border-2 border-emerald-400 shadow-md"
                    />
                  ) : (
                    <div className="w-14 h-14 aspect-square rounded-full shrink-0 bg-emerald-500/80 text-white font-bold flex items-center justify-center text-sm border-2 border-emerald-400 shadow-md">
                      {getInitials(rank3?.name || 'Player 3')}
                    </div>
                  )}
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center border border-slate-900 shadow">
                    3
                  </span>
                </div>
                <div className="pt-1">
                  <p className="text-xs font-bold text-white truncate max-w-[90px]">
                    {rank3?.name || 'Player 3'}
                  </p>
                  <p className="text-sm font-extrabold text-white mt-0.5">
                    {Math.round(rank3?.mmr || 0).toLocaleString('id-ID')}
                  </p>
                  <span className="inline-block text-[10px] text-slate-400 font-medium">
                    {rank3?.tier || 'Silver'}
                  </span>
                </div>
              </div>
            </div>
          </div>

        {/* Posisi Filter Chips */}
        <div className="space-y-2">
          <label className="text-xs font-semibold text-gray-500 block">Posisi</label>
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
            {POSITION_FILTERS.map((pos) => {
              const isActive = selectedPosition === pos.id;
              return (
                <button
                  key={pos.id}
                  type="button"
                  onClick={() => setSelectedPosition(pos.id)}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#FF4D15] text-white shadow-md shadow-orange-500/20'
                      : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Icon icon={pos.icon} className="w-3.5 h-3.5" />
                  <span>{pos.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Peringkat Lengkap List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-[#0E1726]">Peringkat lengkap</h2>
            <span className="text-xs font-medium text-gray-400">Top 50 pemain</span>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-2xs overflow-hidden">
            {/* Table Header */}
            <div className="flex items-center px-4 py-2.5 bg-gray-50/80 border-b border-gray-100 text-[10px] font-bold uppercase tracking-wider text-gray-400">
              <span className="w-8 text-center">#</span>
              <span className="flex-1 pl-2">PEMAIN</span>
              <span className="w-14 text-center">MATCH</span>
              <span className="w-16 text-right">MMR</span>
            </div>

            {/* Skeleton Loading Rows */}
            {isLoading ? (
              <div className="divide-y divide-gray-100">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <div key={i} className="flex items-center px-4 py-3.5 animate-pulse space-x-3">
                    <div className="w-6 h-4 bg-gray-200 rounded" />
                    <div className="w-10 h-10 aspect-square rounded-full bg-gray-200 shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-4 w-28 bg-gray-200 rounded" />
                      <div className="h-3 w-20 bg-gray-100 rounded" />
                    </div>
                    <div className="w-10 h-4 bg-gray-200 rounded" />
                    <div className="w-14 h-4 bg-gray-200 rounded" />
                  </div>
                ))}
              </div>
            ) : rankings.length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm font-medium">
                Belum ada data pemain di posisi ini
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {rankings.map((player) => {
                  const isCurrent = player.isCurrentUser;
                  return (
                    <div
                      key={player.userId}
                      onClick={() => player.username && navigate(`/profile/${player.username}`)}
                      className={`flex items-center px-4 py-3.5 transition-colors ${
                        player.username ? 'cursor-pointer hover:bg-gray-100/80' : ''
                      } ${
                        isCurrent
                          ? 'bg-[#FFF7ED] border-l-4 border-[#FF4D15]'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Rank Number */}
                      <div className="w-8 flex flex-col items-center justify-center">
                        <span className="text-sm font-bold text-gray-900">{player.rank}</span>
                        {player.rankChange && player.rankChange !== '--' && (
                          <span
                            className={`text-[9px] font-bold ${
                              player.rankChange.startsWith('^')
                                ? 'text-emerald-500'
                                : 'text-rose-500'
                            }`}
                          >
                            {player.rankChange}
                          </span>
                        )}
                      </div>

                      {/* Avatar */}
                      <div className="ml-2 mr-3 shrink-0">
                        {player.avatar ? (
                          <img
                            src={player.avatar}
                            alt={player.name}
                            className="w-10 h-10 aspect-square rounded-full object-cover border border-gray-100 shadow-2xs shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 aspect-square rounded-full bg-orange-500 text-white font-bold flex items-center justify-center text-xs shadow-2xs shrink-0">
                            {getInitials(player.name)}
                          </div>
                        )}
                      </div>

                      {/* Name, Tier & City */}
                      <div className="flex-1 min-w-0 pr-2">
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {player.name}
                        </p>
                        <div className="flex items-center gap-1.5 text-[11px] text-gray-400 font-medium">
                          <span className="px-1.5 py-0.2 rounded bg-gray-100 text-gray-600 font-semibold text-[10px]">
                            {player.tier}
                          </span>
                        </div>
                      </div>

                      {/* Match Count */}
                      <div className="w-14 text-center text-xs font-semibold text-gray-500">
                        {player.matches}
                      </div>

                      {/* MMR Value */}
                      <div className="w-16 text-right text-sm font-extrabold text-gray-900">
                        {Math.round(player.mmr).toLocaleString('id-ID')}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* User Rank Card (Normal Layout Flow - Non-sticky) */}
        {currentUser && (
          <div
            onClick={() => currentUser.username && navigate(`/profile/${currentUser.username}`)}
            className={`bg-[#0F172A] text-white rounded-2xl p-4 shadow-lg flex items-center justify-between border border-slate-800 ${
              currentUser.username ? 'cursor-pointer hover:bg-slate-800/90 transition-colors' : ''
            }`}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-base font-extrabold text-amber-400 w-7 text-center">
                #{currentUser.rank}
              </span>
              {currentUser.avatar ? (
                <img
                  src={currentUser.avatar}
                  alt={currentUser.name}
                  className="w-10 h-10 aspect-square rounded-full object-cover border border-slate-700 shadow-md shrink-0"
                />
              ) : (
                <div className="w-10 h-10 aspect-square rounded-full bg-orange-500 text-white font-bold flex items-center justify-center text-xs border border-slate-700 shrink-0">
                  {getInitials(currentUser.name)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-bold text-white truncate">
                  Kamu · {currentUser.name}
                </p>
                <p className="text-[11px] text-slate-400 font-medium truncate">
                  {diffNextText}
                </p>
              </div>
            </div>
            <div className="text-right shrink-0 ml-3">
              <span className="text-base font-extrabold text-white">
                {Math.round(currentUser.mmr).toLocaleString('id-ID')}
              </span>
            </div>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientMMRLeaderboardPage;
